/**
 * QR Issuance Page (管理者向け QR コード発行画面)
 * 3 モードで QR を発行し、1 ページ 1 名の PDF として出力する:
 *  ① 全ユーザー（全社横断）を一括
 *  ② 選択した会社の全員を一括
 *  ③ 指定した 1 名のみ
 * いずれもバックエンドの POST /admin/qr-tokens/bulk（target/companyId/userId で AND 絞り込み）を利用。
 * 認可は adminOnly（運営）。
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import * as qrAuthApi from '../api/qrAuthApi';
import { generateBulkQrPdf } from '../utils/qrPdf';
import { organizationApi } from '../api/organizationApi';
import type { Company } from '../types/organization.types';
import { getUsers, type User } from '../api/userManagementApi';

/** 有効期限（ISO8601）を日本語ロケールの日時表記に整形する。 */
const formatExpiry = (expiresAt: string): string =>
  new Date(expiresAt).toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

/** ダウンロードファイル名に使えない文字を除去する。 */
const safeFileName = (s: string): string => s.replace(/[\\/:*?"<>|]/g, '_').trim() || 'qr';

export const QrIssuancePage = () => {
  // 会社一覧（②③のセレクタ用）
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesError, setCompaniesError] = useState<string | null>(null);

  // ② 会社指定
  const [companyIdAll, setCompanyIdAll] = useState<string>('');

  // ③ 1 名指定
  const [companyIdOne, setCompanyIdOne] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userId, setUserId] = useState<string>('');

  // 発行/PDF 生成の共有状態（同時に走るのは 1 操作のみ）
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  // 会社一覧をロード
  useEffect(() => {
    organizationApi
      .getCompanies()
      .then((list) => setCompanies([...list].sort((a, b) => a.name.localeCompare(b.name, 'ja'))))
      .catch(() => setCompaniesError('会社一覧の取得に失敗しました'));
  }, []);

  // ③ 会社が選ばれたらその会社のユーザーをロード
  useEffect(() => {
    if (!companyIdOne) {
      setUsers([]);
      setUserId('');
      return;
    }
    let aborted = false;
    setLoadingUsers(true);
    setUserId('');
    getUsers({ companyId: companyIdOne, limit: 1000 })
      .then((res) => {
        if (aborted) return;
        setUsers([...res.users].sort((a, b) => a.name.localeCompare(b.name, 'ja')));
      })
      .catch(() => {
        if (!aborted) setError('ユーザー一覧の取得に失敗しました');
      })
      .finally(() => {
        if (!aborted) setLoadingUsers(false);
      });
    return () => {
      aborted = true;
    };
  }, [companyIdOne]);

  // 発行 → PDF 生成の共通処理
  const runIssue = useCallback(
    async (params: qrAuthApi.IssueTokensBulkParams, fileName: string) => {
      try {
        setBusy(true);
        setError(null);
        setProgress(null);

        const { items, issuedCount } = await qrAuthApi.issueTokensBulk(params);
        if (issuedCount === 0 || items.length === 0) {
          setError('対象ユーザーが存在しないため PDF を生成できませんでした');
          return;
        }
        setExpiresAt(items[0].expiresAt);
        setProgress({ done: 0, total: items.length });
        await generateBulkQrPdf(items, fileName, (done, total) => setProgress({ done, total }));
      } catch {
        setError('QR コード PDF の生成に失敗しました');
      } finally {
        setBusy(false);
        setProgress(null);
      }
    },
    []
  );

  const selectedUser = users.find((u) => u.id === userId);
  const companyNameOf = (id: string) => companies.find((c) => c.id === id)?.name ?? id;

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        QR コード発行
      </Typography>

      {companiesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {companiesError}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ① 全ユーザー（全社横断）を一括 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={1}>
            ① 全ユーザーを一括発行（全社横断）
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            全社の全ユーザー分の QR を発行し、1 ページ 1 名の PDF としてダウンロードします。
            ユーザー数が多い場合は生成に時間がかかることがあります。
          </Typography>
          <Button
            variant="contained"
            onClick={() => runIssue({}, 'qr-codes-all-users.pdf')}
            disabled={busy}
            sx={{ minWidth: 260 }}
          >
            {busy ? <CircularProgress size={24} /> : '全ユーザーの QR を一括 PDF 出力'}
          </Button>
        </CardContent>
      </Card>

      {/* ② 会社を選んで全員を一括 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={1}>
            ② 会社を選んで全員を一括発行
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            選択した会社の全ユーザー分の QR を発行し、PDF としてダウンロードします。
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 260 }} disabled={busy}>
              <InputLabel id="qr-company-all-label">会社</InputLabel>
              <Select
                labelId="qr-company-all-label"
                label="会社"
                value={companyIdAll}
                onChange={(e: SelectChangeEvent) => setCompanyIdAll(e.target.value)}
              >
                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              disabled={busy || !companyIdAll}
              onClick={() =>
                runIssue(
                  { companyId: companyIdAll },
                  `qr-codes-${safeFileName(companyNameOf(companyIdAll))}.pdf`
                )
              }
              sx={{ minWidth: 260 }}
            >
              {busy ? <CircularProgress size={24} /> : 'この会社の全員の QR を PDF 出力'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* ③ 1 名を指定して発行 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={1}>
            ③ 1 名を指定して発行
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            会社を選び、対象の 1 名を指定して QR を発行し、その方のみの PDF をダウンロードします。
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 240 }} disabled={busy}>
              <InputLabel id="qr-company-one-label">会社</InputLabel>
              <Select
                labelId="qr-company-one-label"
                label="会社"
                value={companyIdOne}
                onChange={(e: SelectChangeEvent) => setCompanyIdOne(e.target.value)}
              >
                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 280 }}
              disabled={busy || !companyIdOne || loadingUsers}
            >
              <InputLabel id="qr-user-one-label">対象者</InputLabel>
              <Select
                labelId="qr-user-one-label"
                label="対象者"
                value={userId}
                onChange={(e: SelectChangeEvent) => setUserId(e.target.value)}
              >
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}（{u.email}）
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {loadingUsers && <CircularProgress size={20} />}
            <Button
              variant="contained"
              disabled={busy || !userId}
              onClick={() =>
                runIssue({ userId }, `qr-code-${safeFileName(selectedUser?.name ?? 'user')}.pdf`)
              }
              sx={{ minWidth: 220 }}
            >
              {busy ? <CircularProgress size={24} /> : 'この 1 名の QR を PDF 出力'}
            </Button>
          </Stack>
          {companyIdOne && !loadingUsers && users.length === 0 && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              この会社にはユーザーがいません。
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* 共有: 進捗・有効期限 */}
      <Card>
        <CardContent>
          {busy && (
            <Typography variant="body2" color="text.secondary" mb={1}>
              {progress ? `QR を生成中… (${progress.done}/${progress.total})` : 'トークンを発行中…'}
            </Typography>
          )}
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle1" mb={1}>
            有効期限
          </Typography>
          {expiresAt ? (
            <Box>
              <Typography variant="body2" color="text.secondary">
                有効期限（直近の発行分）
              </Typography>
              <Typography variant="h6">{formatExpiry(expiresAt)}</Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              発行を行うと、発行された QR の有効期限がここに表示されます。
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default QrIssuancePage;
